import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { MatDialog } from "@angular/material/dialog";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { MatTableDataSource } from "@angular/material/table";
import { MapInfoWindow } from "@angular/google-maps";
import { CdkDragDrop, CdkDragMove } from "@angular/cdk/drag-drop";
import { ToastrService } from "ngx-toastr";
import { TranslocoService } from "@ngneat/transloco";
import { map, mergeMap, take } from 'rxjs/operators';
import { BehaviorSubject, combineLatest, of, Subject } from 'rxjs';
import { appData, concatIdBusinessId, DRAG_ITEM_SIZE, getResourceFormat, isHostDev, mimeSwap, SUPPORTED_FILE_TYPES } from "../../../../common";
import { SceneMimeTypes } from "../../scene-blocks/mime-constants";
import { StoreSelectorsService, ImportService, LocalStoreService, SceneAddService, ContainerService } from "../../../../services";
import { DialogGetAssetComponent, IDialogGetAsset } from "../../../../components/dialog-get-asset/dialog-get-asset.component";
import { Compbaser } from "../../../../components/compbaser";
import { IAppSettingsWrap, Id, IResource, IResourcePreview, IResourceSharedItem, ISceneAddBlock, ISceneTemplate, ISelectedSceneSceneId } from "../../../../interfaces";
import { actionGetResourcesSharedLibrary, actionGetResourcesSharedLibrarySuccess } from "../../../../store/actions/user.actions";
import { ISceneImportTemplate, sceneTemplates } from 'assets/accountTemplates/scene-template-constants';
import { ITimelineViewMode } from '../../scene-editor/scene-editor.component';
import { FormControl } from '@angular/forms';
import { Lib } from 'libs/Lib';
import * as _ from "lodash";
import { SelectionModel } from '@angular/cdk/collections';

interface IItem {
    id: Id;
    name: string;
    type: string | 'scene';
    mimeType: string;
    resource?: IResource;
}

interface ISelectOption {
    id: string;
    name: string;
    groupIds?: Array<any>;
}

@Component({
    selector: 'app-scene-add-content',
    templateUrl: './scene-add-content.component.html',
    styleUrls: ['./scene-add-content.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SceneAddContentComponent extends Compbaser implements OnInit {

    @Input() addBlockType: ISceneAddBlock;
    @Input() pageSizeOptions: Array<any> = [10, 20, 30];
    @Input() pageSize = 10;

    @Output() addItem: EventEmitter<any> = new EventEmitter<any>();

    @ViewChild(MatSort) sort: MatSort;
    @ViewChild(MatPaginator) paginator: MatPaginator;
    @ViewChild(MapInfoWindow) infoWindow: MapInfoWindow;
    @ViewChild("downloadModal", { read: TemplateRef }) downloadModal: TemplateRef<any>;
    @ViewChild("importPopup", { read: TemplateRef }) importPopup: TemplateRef<any>;

    coreAppData: IAppSettingsWrap;
    changes$: Subject<number> = new Subject();
    arrayResourcesScenes: Array<IItem> = [];
    filter = '';
    dataSource: MatTableDataSource<any>;
    displayedColumns: string[] = ['select', 'name', 'type'];
    sceneId: Id;
    sceneMime = '';
    hiddenPanel = true;
    resourceName: string;
    activeBlockId;
    blockSmallView = false;
    contentMode = '';
    m_resourceModes = ['resources', 'library', 'upload', 'ai', 'stock'];
    m_sceneModes = ['scenes', 'library'];
    selectedResourceTab = 0;
    m_importSceneData: IDialogGetAsset;
    m_sceneTemplates: Array<ISceneTemplate> = [];
    currentSceneTemplates: Array<ISceneTemplate> = [];
    categorySceneOptions: Array<ISelectOption> = [];
    categoryResOptions: Array<ISelectOption> = [];
    categoryControl = new FormControl(['']);
    selectedItem = {
        type: 'scene',
        title: '',
        category: '',
        sceneId: '-1',
        nativeId: '-1',
        businessId: '-1',
        resourceId: '-1',
        height: '1920',
        width: '1080',
        url: '',
        videoUrl: ''
    };

    imageLoading = true;
    imageImporting = false;
    sharedResources: Array<IResourceSharedItem> = [];
    m_sharedResources: Array<IResourcePreview> = [];
    currentResources: Array<IResourcePreview> = [];
    dataSourceThumbnails$: BehaviorSubject<IItem[]>;
    ready = false;
    viewMode: ITimelineViewMode;
    isMouseOnScene = false;
    isMouseOnPlaylist = false;
    isGridView = false;
    selection: SelectionModel<number>;

    throttle = 300;
    scrollDistance = 2;
    initialCount = 50;
    verticalMoreCount = 20;

    debouncedDraggingEvent;

    readonly dragItemSize = DRAG_ITEM_SIZE;

    constructor(
        private translocoService: TranslocoService,
        public storeSelector: StoreSelectorsService,
        private localStoreService: LocalStoreService,
        private containerService: ContainerService,
        protected override dialog: MatDialog,
        private cdr: ChangeDetectorRef,
        private toastr: ToastrService,
        private sceneAddService: SceneAddService,
        private importService: ImportService) {
        super();
        this.debouncedDraggingEvent = _.debounce((e) => {
            this.dragItem(e);
        }, 200);
    }

    ngOnInit() {
        this.cod(
            this.storeSelector.getCoreData()
                .subscribe((i_coreData: IAppSettingsWrap) => this.coreAppData = i_coreData)
        );
        this.cod(
            this.storeSelector.listenSelectedSceneIdAndSceneItemId()
                .pipe(
                    mergeMap((v: ISelectedSceneSceneId) => {
                        this.sceneId = v.sceneId;
                        /** improve performance, if sceneMime is already set no need to check again **/
                        if (this.sceneMime) {
                            return of(null);
                        } else {
                            return this.storeSelector.getSceneMimeType(this.sceneId);
                        }
                    })
                )
                .subscribe((v) => {
                    if (!v) return;
                    this.sceneMime = v;
                    this.m_importSceneData = { mode: "SCENE:PLAYER", mimeType: this.sceneMime };
                })
        );

        this.cod(
            this.importService.progress$.subscribe(resourceName => {
                this.resourceName = String(resourceName);
                this.cdr.markForCheck();
            })
        );

        this.cod(
            this.containerService.editorMode
                .subscribe((mode: ITimelineViewMode) => {
                    if (mode) {
                        this.viewMode = mode;
                    } else {
                        this.viewMode = null;
                    }
                })
        );
        this.cod(
            this.containerService.isMouseOnScene.subscribe((v) => {
                this.isMouseOnScene = v;
            })
        );
        this.cod(
            this.containerService.isMouseOnPlaylist.subscribe((v) => {
                this.isMouseOnPlaylist = v;
            })
        );

        if (this.addBlockType === 'BLOCK_RESOURCES') {
            this.contentMode = 'resources';
            this.isGridView = true;
            // this.contentMode = 'library';
            this.initResourceTable();
            this.composeResourceCategories();
        }

        if (this.addBlockType === 'BLOCK_SCENES') {
            this.contentMode = 'scenes';
            // this.contentMode = 'library';
            this.initSceneTable();
            this.composeSceneCategories();
        }

        this.sceneAddService.setBlockType(this.addBlockType);
        this.blockSmallView = this.sceneAddService.blockSmallView;
        this.selection = new SelectionModel<number>(false, []);

        setTimeout(() => {
            this.changes$.next(Math.random());
            this.hiddenPanel = false;
            this.cdr.markForCheck();
        }, 500);
    }

    private composeSceneCategories() {
        this.categorySceneOptions = [];
        Object.keys(sceneTemplates).forEach((templateName) => {
            const template: ISceneImportTemplate = {};
            template[templateName] = sceneTemplates[templateName];
            const businessId = template[templateName][0];
            const templateId = template[templateName][2];
            const folder = template[templateName][4].folder;
            const indexOf = this.categorySceneOptions.findIndex((v) => v.name === folder);
            if (indexOf === -1) {
                this.categorySceneOptions.push({
                    id: _.uniqueId('__'),
                    name: folder,
                    groupIds: []
                });
            } else {
                this.categorySceneOptions[indexOf].groupIds.push(concatIdBusinessId(templateId, businessId, 1));
            }
        });
        this.categoryControl.setValue(this.categorySceneOptions.map((opt) => opt.id));
        this.updateSceneCategories();
    }

    private composeResourceCategories() {
        this.getResourcesLibrary();
        this.cod(
            this.storeSelector.getResourceSharedLibrary().subscribe((res) => {
                this.localStoreService.setStorageSharedLibrary(res);
                this.categoryResOptions = [];
                if (!res.library) return;
                this.sharedResources = res.library;

                this.sharedResources.forEach((res) => {
                    if (SUPPORTED_FILE_TYPES.indexOf(res.$.resourceType.toLowerCase()) === -1) return;
                    const treePath = res.$.treePath;
                    const indexOf = this.categoryResOptions.findIndex((v) => v.name === treePath);
                    this.categoryResOptions = this.categoryResOptions.sort((a, b) => {
                        if (a.name > b.name) return 1;
                        if (b.name > a.name) return -1;
                        return 0;
                    });
                    if (indexOf === -1) {
                        this.categoryResOptions.push({
                            id: _.uniqueId('__'),
                            name: treePath,
                        });
                    }
                });
            })
        );
    }

    private getResourcesLibrary() {
        const lib = this.localStoreService.getSharedResourceLibrary();
        if (lib && !Lib.DevMode() && !isHostDev()) {
            return this.storeSelector.dispatch(actionGetResourcesSharedLibrarySuccess({ value: lib }));
        }
        console.log('getting fresh shared library resources...');
        this.storeSelector.dispatch(actionGetResourcesSharedLibrary({
            config: {
                'ida.signage.me': []
            }
        }));
    }

    /** compose scene templates */
    private composeSceneTemplates(mimeTypeLong: string, sceneList: Array<string>) {
        this.m_sceneTemplates = [];
        if (mimeTypeLong) {
            const mimeTypeLongs: Array<string> = this.storeSelector.getBlockLabelByMimeType(mimeTypeLong, false).types;
            // tslint:disable-next-line
            for (const sceneName in sceneTemplates) {
                const sceneConfig = sceneTemplates[sceneName];
                if (mimeTypeLongs.findIndex((v) => v === sceneConfig[3]) === -1) continue;
                this.m_sceneTemplates.push({
                    nativeId: sceneConfig[2] as any,
                    sceneTitle: sceneConfig[4].name,
                    sceneId: sceneConfig[2] as any,
                    sceneName,
                    height: '',
                    width: '',
                    mimeType: sceneConfig[3],
                    businessId: sceneConfig[0] as any,
                    url: `./assets/images/scenes/${sceneName}.jpg`
                });
            }
        } else if (sceneList) {
            // tslint:disable-next-line
            for (const sceneName in sceneTemplates) {
                const sceneConfig = sceneTemplates[sceneName];
                const id = concatIdBusinessId(sceneConfig[2], sceneConfig[0], 1);
                const idx = sceneList.findIndex(i => i === id);
                if (idx === -1) continue;
                this.m_sceneTemplates.push({
                    nativeId: sceneList[idx],
                    sceneTitle: sceneConfig[4].name,
                    sceneId: sceneConfig[2] as any,
                    height: '',
                    width: '',
                    mimeType: sceneConfig[3],
                    sceneName,
                    businessId: sceneConfig[0] as any,
                    url: `./assets/images/scenes/${sceneName}.jpg`
                });
            }
        }
        this.currentSceneTemplates = this.m_sceneTemplates.slice(0, this.initialCount);
        this.cdr.detectChanges();
    }

    private initResourceTable() {
        const resources$ = this.storeSelector.getResources();
        this.cod(
            combineLatest([resources$, this.changes$]).pipe(map(([i_resources, i_changes]) => {
                this.arrayResourcesScenes = i_resources.map((item) => ({
                    name: item.resource_name,
                    type: item.resource_type,
                    id: item.resource_id,
                    mimeType: '',
                    resource: item
                }));
            })).subscribe(() => {
                this.applyTableCustomization(this.arrayResourcesScenes);
            })
        );
    }

    private initSceneTable() {
        const scenes$ = this.storeSelector.getScenes();
        this.cod(
            combineLatest([scenes$, this.changes$]).pipe(map(([i_scenes, i_changes]) => {
                this.arrayResourcesScenes = i_scenes.filter((item) => this.sceneId !== item.Player.$.id).map((item) => ({
                    name: item.Player.$.label,
                    id: item.Player.$.id,
                    type: 'scene',
                    mimeType: mimeSwap("longToShort", item.Player.$.mimeType)
                }));
            })).subscribe(() => {
                this.applyTableCustomization(this.arrayResourcesScenes);
            })
        );
    }

    private applyTableCustomization(data) {
        if (this.dataSource) {
            this.dataSource.data = data;
        } else {
            this.dataSource = new MatTableDataSource(data);
        }
        this.dataSourceThumbnails$ = this.dataSource.connect();

        this.dataSource.sort = this.sort;
        this.dataSource.paginator = this.paginator;
        this.dataSource.filterPredicate = (data: any, filter) => {
            const dataStr = JSON.stringify(data).toLowerCase();
            return dataStr.indexOf(filter) !== -1;
        };
        this.dataSource.sortingDataAccessor = (item: IItem, property) => {
            return item[property];
        };
        this.applyFilter(this.filter);
        this.cdr.markForCheck();
    }

    private isMimeSet(): boolean {
        return !(this.sceneMime === '' || _.isUndefined(this.sceneMime));
    }

    /** Load more scene templates when scroll down */
    loadMoreSceneTemplates() {
        const cnt = this.currentSceneTemplates.length;
        if (cnt < this.m_sceneTemplates.length) {
            const moreCount = this.blockSmallView ? (2 * this.verticalMoreCount) : this.verticalMoreCount;
            this.currentSceneTemplates.push(...this.m_sceneTemplates.slice(cnt, cnt + moreCount));
        }
        this.cdr.detectChanges();
    }

    /** Load more resources when scroll down */
    loadMoreResources() {
        const cnt = this.currentResources.length;
        if (cnt < this.m_sharedResources.length) {
            const moreCount = this.blockSmallView ? (2 * this.verticalMoreCount) : this.verticalMoreCount;
            this.currentResources.push(...this.m_sharedResources.slice(cnt, cnt + moreCount));
        }
        this.cdr.detectChanges();
    }

    onFilterSelection(e) {
        this.clearSelection();
        // this.m_EnumConnectionSelected = e.value[0];
        this.changes$.next(Math.random());
    }

    updateSceneCategories() {
        let sceneIds = [];
        if (this.categoryControl.value) {
            this.categoryControl.value.forEach((_id) => {
                const item = this.categorySceneOptions.find((option) => option.id == _id);
                if (item) {
                    sceneIds = sceneIds.concat(item.groupIds);
                }
            });
        }
        this.composeSceneTemplates('', sceneIds);
    }

    updateResourceCategories() {
        let selectedRes = [];
        if (this.categoryControl.value) {
            this.categoryControl.value.forEach((_id) => {
                const item = this.categoryResOptions.find((option) => option.id == _id);
                if (item) {
                    selectedRes.push(item.name);
                }
            });
        }
        this.composeResourceTemplates(this.sharedResources.filter((r) => selectedRes.indexOf(r.$.treePath) > -1));
    }

    /** compose scene templates */
    private composeResourceTemplates(i_resources: Array<IResourceSharedItem>) {
        this.m_sharedResources = [];
        if (i_resources) {
            i_resources.forEach((r, index) => {
                const res = getResourceFormat(r.$);
                res.resourceId = r.$.resourceId;
                res.businessId = r.$.businessId;
                res.name = r.$.resourceName;
                res.treePath = r.$.treePath;
                res.resourceType = r.$.resourceType;
                if (!res.url) res.url = r.url;
                if (res.resourceType === 'mp4') {
                    res.videoUrl = r.url;
                }
                res.show = true;
                this.m_sharedResources.push(res);
            });
            this.currentResources = this.m_sharedResources.slice(0, this.initialCount);
            this.ready = true;
        }
    }

    openAssociateDialog() {
        let data: IDialogGetAsset;
        if (this.addBlockType === 'BLOCK_SCENES') {
            if (this.contentMode === 'library') {
                data = { mode: "SCENE:LIBRARY", mimeType: SceneMimeTypes.Player, multiSelect: true };
            } else {
                data = { mode: "SCENE:ALL", mimeType: null, multiSelect: true };
            }
        } else if (this.addBlockType === 'BLOCK_RESOURCES') {
            if (this.contentMode === 'library') {
                data = { mode: "RESORUCE:LIBRARY", multiSelect: true };
            } else {
                data = { mode: "RESOURCE:ALL", multiSelect: true };
            }
        } else {
            return;
        }

        const dialogRefVal = this.dialog.open(DialogGetAssetComponent, {
            width: '70%',
            panelClass: 'custom-dlg-container',
            data
        });
        this.cod(
            dialogRefVal.afterClosed()
                .pipe(take(1))
                .subscribe((dialogGetAssets: IDialogGetAsset[]) => {
                    if (!dialogGetAssets || !Array.isArray(dialogGetAssets)) return;
                    this.addBlock(dialogGetAssets.map((dialogGetAsset) => ({
                        id: dialogGetAsset.selected,
                        type: dialogGetAsset.sourceData.resource_type ? dialogGetAsset.sourceData.resource_type : 'scene',
                        mimeType: dialogGetAsset.sourceData?.Player?.$?.mimeType ? dialogGetAsset?.sourceData?.Player?.$?.mimeType : '',
                        name: dialogGetAsset.sourceData?.resource_name ? dialogGetAsset?.sourceData?.resource_name : dialogGetAsset?.sourceData?.Player?.$?.label
                    })));
                })
        );
    }

    async importLibrary(event = null, dropComponent = null) {
        this.dialog.closeAll();
        this.resourceName = this.translocoService.translate('ai.downloading', {}, this.translocoService.getActiveLang());
        this.activeBlockId = null;

        const dialogRef = this.dialog.open(this.downloadModal, {
            height: '400px',
            width: '400px',
            panelClass: 'transparentDialog',
            hasBackdrop: false,
            data: {}
        });

        try {
            if (this.addBlockType === 'BLOCK_SCENES') {
                this.activeBlockId = await this.importService.importScene(this.selectedItem.businessId, this.selectedItem.sceneId, this.selectedItem.title, false, true);
                // this.activeBlockId = await this.importService.importScene(595375, 120, this.selectedItem.title, false, true);
            } else if (this.addBlockType === 'BLOCK_RESOURCES') {
                this.activeBlockId = await this.importService.importResource(this.selectedItem.businessId, this.selectedItem.resourceId);
            }
            this.sceneAddService.setActiveBlockId(this.activeBlockId);
            if (this.activeBlockId) {
                if (event) {
                    this.sceneAddService.dropBlock(event, dropComponent, true);
                } else {
                    this.sceneAddService.addBlock(this.selectedItem, this.viewMode, true);
                }
                this.cdr.detectChanges();
            }

        } catch (err) {
            console.log(err);

        } finally {
            setTimeout(() => {
                dialogRef.close();
            }, 1200);
        }
    }

    showImportDialog(data) {
        if (!data) return;
        this.setSelectedItem(data);

        if (this.selectedItem.title !== 'mp4') {
            this.imageLoading = true;
            this.cdr.detectChanges();
        }

        const helpModalDiag = this.dialog.open(this.importPopup, {
            width: '600px',
            data: {
                callBack: (data) => {
                    helpModalDiag.close();
                }
            }
        });
        helpModalDiag.afterClosed().subscribe(result => {
            this.imageLoading = false;
        });

        setTimeout(() => {
            if (this.imageLoading) {
                this.toastr.error(this.translocoService.translate('toast.notFoundImage', {}, this.translocoService.getActiveLang()));
                this.onClose();
            }
        }, 15000);
    }

    addBlock(event) {
        if (this.addItem.observed) {
            this.addItem.emit(event);
            return;
        }

        this.sceneAddService.addBlock(event, this.viewMode, true);
        this.cdr.detectChanges();
    }

    dropBlock(event: CdkDragDrop<any>) {
        if (this.addItem.observed) {
            this.addItem.emit(event);
            return;
        }
        if (!this.checkDropZone(event)) {
            return;
        }

        this.sceneAddService.dropBlock(event, this.isMouseOnPlaylist ? "playlist" : "scene", true);
        this.cdr.detectChanges();
    }

    dropLibraryItem(event: CdkDragDrop<any>) {
        if (!this.checkDropZone(event)) {
            return;
        }

        this.setSelectedItem(event.item.data);
        this.importLibrary(event, this.isMouseOnPlaylist ? "playlist" : "scene");
    }

    dragItem(event: CdkDragMove<any>) {
        this.sceneAddService.dragBlock(event, this.isMouseOnPlaylist ? "playlist" : "scene");
    }

    onDraggingItem(event: CdkDragMove<any>) {
        if (!this.isMouseOnPlaylist && !this.isMouseOnScene) {
            return;
        }
        this.debouncedDraggingEvent(event);
    }

    private checkDropZone(event) {
        let enabled = true;
        if (!event || event.isPointerOverContainer) {
            enabled = false;
        }
        if (!this.isMouseOnPlaylist && !this.isMouseOnScene) {
            this.toastr.warning(this.translocoService.translate('toast.notDropArea', {}, this.translocoService.getActiveLang()));
            enabled = false;
        }
        // if (!enabled) {
        //     this.containerService.setDndContent(null);
        // }
        return enabled;
    }

    private setSelectedItem(data) {
        if (!data) return;

        if (this.addBlockType === 'BLOCK_SCENES') {
            this.selectedItem.type = 'scene';
            this.selectedItem.nativeId = data.nativeId;
            this.selectedItem.sceneId = data.sceneId;
            this.selectedItem.businessId = data.businessId;
            this.selectedItem.title = data.sceneName;
            this.selectedItem.url = `${appData.cloudResourcesS3}/business1000/resources/scenes_snaps/${this.selectedItem.title}.png`;
        } else if (this.addBlockType === 'BLOCK_RESOURCES') {
            this.selectedItem.type = data.resourceType;
            this.selectedItem.businessId = data.businessId;
            this.selectedItem.resourceId = data.resourceId;
            this.selectedItem.category = data.treePath;
            this.selectedItem.title = data.name;
            this.selectedItem.url = data.url;
            this.selectedItem.videoUrl = data.videoUrl;
        }
    }

    onLoad(e) {
        this.imageLoading = false;
        this.cdr.detectChanges();
    }

    toggleBlockSize() {
        this.blockSmallView = !this.blockSmallView;
        this.sceneAddService.setBlockView(this.blockSmallView);
        if (this.blockSmallView) {
            if (this.addBlockType === 'BLOCK_SCENES') {
                this.loadMoreSceneTemplates();
            } else if (this.addBlockType === 'BLOCK_RESOURCES') {
                this.loadMoreResources();
            }
        }
        this.cdr.detectChanges();
    }

    onChangePagination(event) {
        if (!this.dataSource) return;
        this.dataSourceThumbnails$ = this.dataSource.connect();
        if (event.pageSize == this.pageSize) {
            window.stop();
        }
        this.cdr.detectChanges();
    }

    applyFilter(filterValue: string) {
        if (!this.dataSource) return;
        this.dataSource.filter = filterValue.trim().toLowerCase();
    }

    toggleContentMode(event) {
        this.contentMode = event.value;
    }

    suppressKeyEvent(event) {
        event.stopPropagation();
    }

    onSelectedThumb(resource) {
        this.selection.toggle(resource.resource_id);
        this.cdr.detectChanges();
    }

    clearSelection() {
        this.filter = '';
        this.applyFilter('');
    }

    selectTabOfResources(event) {
        const array = ['resources', 'library', 'upload', 'ai', 'stock'];
        this.contentMode = array[event.index];
    }

    onResourceTabClick(index: number) {
        this.selectedResourceTab = index;
        const array = ['resources', 'library', 'upload', 'ai', 'stock'];
        this.contentMode = array[index];
    }

    selectTabOfScenes(event) {
        const array = ['scenes', 'library'];
        this.contentMode = array[event.index];
    }

    onClose() {
        this.imageLoading = false;
        this.dialog.closeAll();
    }

    override destroy() {
        this.sceneAddService.setActiveBlockId(null);
        super.destroy();
    }
}
